import { getMimeType } from './ufile/ufile-utils.js'
import { UFile, } from './ufile/ufile.js'

// zip.js loads lazily inside the two functions: as a static import, this
// barrel-exported module dragged ~320KB of zip machinery into every bundle
// that imported anything from @w/utils, zip user or not.
export async function archive(files: Record<string, Blob>): Promise<Blob> {
  const { BlobReader, BlobWriter, ZipWriter } = await import('@zip.js/zip.js')
  const zipWriter = new ZipWriter(new BlobWriter('application/zip'))

  for (const [path, blob] of Object.entries(files)) {
    await zipWriter.add(path, new BlobReader(blob))
  }

  return await zipWriter.close()
}

function getMimeTypeFromFilename(filename: string): string {
  const extension = filename.toLowerCase().match(/\.([^.]+)$/)
  if (!extension) return 'application/octet-stream'
  return getMimeType(`.${extension[1]}`)
}

function findCommonRootPrefix(filenames: string[]): string {
  if (filenames.length === 0) return ''

  // Filter out macOS metadata paths for prefix detection
  const cleanFilenames = filenames.filter((name) => !name.startsWith('_MACOSX/') && !name.startsWith('__MACOSX/'))
  if (cleanFilenames.length === 0) return ''

  // Find the shortest path to avoid index out of bounds
  const shortestPath = cleanFilenames.reduce((shortest, current) =>
    current.length < shortest.length ? current : shortest,
  )

  let commonPrefix = ''
  const pathParts = shortestPath.split('/')

  // Check each path segment to find common root
  for (let i = 0; i < pathParts.length - 1; i++) {
    // -1 to exclude filename
    const testPrefix = `${pathParts.slice(0, i + 1).join('/')}/`

    if (cleanFilenames.every((filename) => filename.startsWith(testPrefix))) {
      commonPrefix = testPrefix
    } else {
      break
    }
  }

  return commonPrefix
}

export interface UnarchiveOptions {
  flatten?: boolean
}

export async function unarchive(zip: Blob | UFile, options: UnarchiveOptions = {}): Promise<Record<string, UFile>> {
  const { BlobReader, BlobWriter, ZipReader } = await import('@zip.js/zip.js')
  const zipBlob = zip instanceof UFile ? zip.blob : zip
  const zipReader = new ZipReader(new BlobReader(zipBlob))
  const entries = await zipReader.getEntries()
  const files: Record<string, UFile> = {}

  // Get all file entries (non-directories) and ignore _MACOSX and __MACOSX files
  const fileEntries = entries.filter(
    (entry) => !entry.directory && !entry.filename.startsWith('_MACOSX/') && !entry.filename.startsWith('__MACOSX/'),
  )
  const filenames = fileEntries.map((entry) => entry.filename)

  // Find common root prefix to remove (only if flatten option is enabled)
  const commonPrefix = options.flatten ? findCommonRootPrefix(filenames) : ''

  for (const entry of fileEntries) {
    if (entry.getData) {
      const writer = new BlobWriter()
      const originalBlob = await entry.getData(writer)

      // Remove common root prefix if it exists
      let filename = entry.filename
      if (commonPrefix && filename.startsWith(commonPrefix)) {
        filename = filename.substring(commonPrefix.length)
      }

      // Skip if filename becomes empty after prefix removal
      if (!filename) continue

      // Restore the correct MIME type based on file extension
      const mimeType = getMimeTypeFromFilename(filename)
      const blob = new Blob([originalBlob], { type: mimeType })

      // Create UFile with the correct path and type
      files[filename] = new UFile(blob, { path: filename, type: mimeType })
    }
  }

  await zipReader.close()
  return files
}
